import { useCallback, useEffect, useState } from 'react';
import axios from '../../utils/axios';
import { getAppDocsByApp } from '../../utils/urls';

// App docs from the paginated /api/app-docs/app/<id>/ endpoint. Every load
// re-fetches pages 1..n so uploads/deletes made meanwhile can't shift items
// between pages and get skipped or duplicated.
export default function useAppDocs(appId, enabled) {
    const [appDocs, setAppDocs]   = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore]   = useState(false);
    const [loading, setLoading]   = useState(false);

    const load = useCallback(async (pages) => {
        if (!appId) return;
        setLoading(true);
        try {
            const settled = await Promise.allSettled(
                Array.from({ length: pages }, (_, i) => axios.get(`${getAppDocsByApp}${appId}/?page=${i + 1}`))
            );
            // Trailing pages can 404 if docs were deleted since they were loaded.
            const loaded = settled.filter(r => r.status === 'fulfilled' && r.value.status === 200).map(r => r.value.data);
            // A failed page also stops paging, so infinite scroll can't retry in a loop.
            if (!loaded.length) { setHasMore(false); return; }
            const seen = new Set();
            setAppDocs(loaded.flatMap(d => d.results || []).filter(d => !seen.has(d.app_doc_id) && seen.add(d.app_doc_id)));
            setPageCount(loaded.length);
            setTotalCount(loaded[loaded.length - 1].count || 0);
            setHasMore(loaded.length === pages && !!loaded[loaded.length - 1].next);
        } finally {
            setLoading(false);
        }
    }, [appId]);

    useEffect(() => {
        if (enabled) load(1);
    }, [enabled, load]);

    const reloadAppDocs   = useCallback(() => load(pageCount), [load, pageCount]);
    const loadMoreAppDocs = useCallback(() => load(pageCount + 1), [load, pageCount]);

    return { appDocs, setAppDocs, reloadAppDocs, loadMoreAppDocs, appDocsHasMore: hasMore, appDocsLoading: loading, appDocsTotal: totalCount };
}
